<template>
  <div class="list-container estimate-list">
    <div ref="topFixArea" class="top-fix-area">
      <div class="estimate-list-header d-flex justify-content-between align-items-center mb-2 gap-2">
      <h1 class="page-title mb-0">{{ pageTitle }}</h1>
      <div class="d-flex flex-wrap justify-content-end gap-2">
        <router-link
          v-if="canCreate && !approvalOnly"
          class="btn btn-primary"
          :to="{ name: 'EstimateCreate', query: { layout: 'portrait_temporary' } }"
        >
          新規作成（縦型）
        </router-link>
        <router-link
          v-if="canCreate && !approvalOnly"
          class="btn btn-primary"
          :to="{ name: 'EstimateCreate', query: { layout: 'landscape_detailed' } }"
        >
          新規作成（横型・詳細）
        </router-link>
      </div>
      </div>

      <div v-if="!approvalOnly" class="mb-2">
        <button
          class="btn btn-sm btn-outline-secondary"
          type="button"
          :aria-expanded="isSearchExpanded"
          aria-controls="estimate-search-panel"
          @click="isSearchExpanded = !isSearchExpanded"
        >
          {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
        </button>
      </div>

      <SearchPanelWrapper
        v-if="!approvalOnly"
        :key="route.fullPath"
        v-model="isSearchExpanded"
        :get-table-element="() => tableWrapper"
      >
        <div id="estimate-search-panel" class="card">
          <div class="card-header fw-bold">検索条件</div>
          <div class="card-body">
            <form class="row g-3" @submit.prevent="search">
          <div class="col-md-3">
            <label class="form-label">見積番号</label>
            <input v-model.trim="filters.estimate_no" class="form-control" type="text">
          </div>
          <div class="col-md-3">
            <label class="form-label">件名</label>
            <input v-model.trim="filters.title" class="form-control" type="text">
          </div>
          <div class="col-md-3">
            <label class="form-label">顧客</label>
            <select v-model="filters.customer_id" class="form-select">
              <option value="">すべて</option>
              <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                {{ customer.customer_nm }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">建物</label>
            <select v-model="filters.building_id" class="form-select">
              <option value="">すべて</option>
              <option v-for="building in filteredBuildings" :key="building.id" :value="building.id">
                {{ building.building_nm }}
              </option>
            </select>
          </div>

          <div v-if="!approvalOnly" class="col-md-2">
            <label class="form-label">承認状態</label>
            <select v-model="filters.approval_status" class="form-select">
              <option value="">すべて</option>
              <option v-for="(label, value) in approvalStatusLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">営業状態</label>
            <select v-model="filters.sales_status" class="form-select">
              <option value="">すべて</option>
              <option v-for="(label, value) in salesStatusLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">作業作成状態</label>
            <select v-model="filters.conversion_status" class="form-select">
              <option value="">すべて</option>
              <option v-for="(label, value) in conversionStatusLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">見積種別</label>
            <select v-model="filters.estimate_type" class="form-select">
              <option value="">すべて</option>
              <option v-for="(label, value) in estimateTypeLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">帳票形式</label>
            <select v-model="filters.output_layout_type" class="form-select">
              <option value="">すべて</option>
              <option v-for="(label, value) in outputLayoutTypeLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">発行日（開始）</label>
            <input v-model="filters.issue_date_from" class="form-control" type="date">
          </div>
          <div class="col-md-2">
            <label class="form-label">発行日（終了）</label>
            <input v-model="filters.issue_date_to" class="form-control" type="date">
          </div>
          <div class="col-md-2">
            <label class="form-label">削除状態</label>
            <select v-model="filters.deleted_status" class="form-select">
              <option value="active">未削除</option>
              <option value="deleted">削除済み</option>
              <option value="all">すべて</option>
            </select>
          </div>

          <div class="col-12 text-end">
            <button class="btn btn-primary" type="submit">検索</button>
            <button class="btn btn-secondary ms-2" type="button" @click="clearSearch">クリア</button>
          </div>
            </form>
          </div>
        </div>
      </SearchPanelWrapper>
    </div>

    <div v-if="allErrorMessages.length" class="alert alert-danger">
      <div v-for="message in allErrorMessages" :key="message">{{ message }}</div>
    </div>

    <div
      v-if="!listLoadFailed"
      class="result-navigation d-flex justify-content-between align-items-center gap-2"
    >
      <div class="text-muted small">検索結果：{{ meta.total_count || 0 }}件</div>
      <Pagination
        :current-page="meta.current_page"
        :total-pages="Math.max(meta.total_pages, 1)"
        @change-page="changePage"
      />
    </div>

    <div ref="tableWrapper" class="table-wrapper">
      <table class="table table-bordered table-hover align-middle mb-0 estimate-table">
        <thead>
          <tr>
            <th class="action-column">操作</th>
            <th><button class="sort-button" type="button" @click="changeSort('estimate_no')">見積番号 {{ sortIndicator('estimate_no') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('title')">件名 {{ sortIndicator('title') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('customer')">顧客／建物 {{ sortIndicator('customer') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('issue_date')">発行日 {{ sortIndicator('issue_date') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('output_layout_type')">帳票形式 {{ sortIndicator('output_layout_type') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('approval_status')">承認 {{ sortIndicator('approval_status') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('sales_status')">営業 {{ sortIndicator('sales_status') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('conversion_status')">作業作成状態 {{ sortIndicator('conversion_status') }}</button></th>
            <th><button class="sort-button" type="button" @click="changeSort('total_amount')">税込合計 {{ sortIndicator('total_amount') }}</button></th>
            <th>作業・請求側（税抜）／差額</th>
            <th><button class="sort-button" type="button" @click="changeSort('created_by_user')">作成者 {{ sortIndicator('created_by_user') }}</button></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="estimate in estimates"
            :key="estimate.id"
            :class="{ 'table-secondary': estimate.del_flg === 1 }"
          >
            <td class="action-column">
              <div class="row-actions">
                <button
                  v-if="approvalOnly && estimate.permissions?.can_approve"
                  class="btn btn-sm btn-primary"
                  type="button"
                  @click="showApprove(estimate)"
                >
                  承認する
                </button>
                <button
                  v-if="approvalOnly && estimate.permissions?.can_reject"
                  class="btn btn-sm btn-outline-danger"
                  type="button"
                  @click="showReject(estimate)"
                >
                  差し戻す
                </button>
                <router-link
                  class="btn btn-sm btn-info"
                  :to="{ name: 'EstimateDetail', params: { id: estimate.id }, query: activeQuery }"
                >
                  詳細
                </router-link>
                <router-link
                  v-if="estimate.permissions?.can_convert && estimate.is_latest_version"
                  class="btn btn-sm btn-primary"
                  :to="{
                    name: 'EstimateWorkConversionPreview',
                    params: { id: estimate.id },
                    query: activeQuery
                  }"
                >
                  次へ：作業作成
                </router-link>
                <router-link
                  v-if="estimate.permissions?.can_edit"
                  class="btn btn-sm btn-success"
                  :to="{
                    name: 'EstimateEdit',
                    params: { id: estimate.id },
                    query: { ...activeQuery, edit_origin: 'list' }
                  }"
                >
                  編集
                </router-link>
                <button
                  v-else-if="estimate.del_flg !== 1"
                  class="btn btn-sm btn-success"
                  type="button"
                  disabled
                  title="承認済みまたは作業作成済みの見積は編集できません"
                >
                  編集
                </button>
                <button
                  v-if="estimate.del_flg !== 1"
                  class="btn btn-sm btn-outline-dark"
                  type="button"
                  :disabled="generatingPdfId === estimate.id || !estimate.permissions?.can_output_pdf"
                  @click="outputPdf(estimate)"
                >
                  {{ generatingPdfId === estimate.id ? 'PDF作成中...' : 'PDF出力' }}
                </button>
                <button
                  v-if="estimate.del_flg !== 1"
                  class="btn btn-sm btn-outline-success"
                  type="button"
                  :disabled="isDuplicating || !estimate.permissions?.can_copy"
                  @click="showCopy(estimate)"
                >
                  複製
                </button>
                <button
                  v-if="estimate.permissions?.can_delete"
                  class="btn btn-sm btn-danger"
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#deleteModal"
                  @click="handleDeleteClick(estimate.id)"
                >
                  削除
                </button>
                <button
                  v-else-if="estimate.del_flg !== 1"
                  class="btn btn-sm btn-danger"
                  type="button"
                  disabled
                  title="承認済みまたは作業作成済みの見積は削除できません"
                >
                  削除
                </button>
              </div>
            </td>
            <td class="text-nowrap">
              {{ estimate.estimate_no }}
              <small v-if="estimate.revision_no > 0" class="text-muted">
                {{ formatRevisionLabel(estimate.revision_no) }}
              </small>
            </td>
            <td>{{ estimate.title }}</td>
            <td>
              <div>{{ estimate.customer?.customer_nm || '-' }}</div>
              <small class="text-muted">{{ estimate.building?.building_nm || '-' }}</small>
            </td>
            <td class="text-nowrap">{{ formatIsoDate(estimate.issue_date) }}</td>
            <td class="text-nowrap">{{ outputLayoutTypeLabels[estimate.output_layout_type] || '-' }}</td>
            <td class="text-center"><span :class="statusBadgeClass('approval', estimate.approval_status)">{{ approvalStatusLabels[estimate.approval_status] }}</span></td>
            <td class="text-center"><span :class="statusBadgeClass('sales', estimate.sales_status)">{{ salesStatusLabels[estimate.sales_status] }}</span></td>
            <td class="text-center"><span :class="statusBadgeClass('conversion', estimate.conversion_status)">{{ conversionStatusLabels[estimate.conversion_status] }}</span></td>
            <td class="text-nowrap text-end fw-bold">{{ formatCurrency(estimate.total_amount) }}</td>
            <td class="text-nowrap text-end operational-amount-cell">
              <template v-if="estimate.work_or_billing_amount_pending">
                <span class="badge text-bg-warning">実施月未設定</span>
              </template>
              <template v-else-if="estimate.work_or_billing_amount != null">
                <div>{{ formatCurrency(estimate.work_or_billing_amount) }}</div>
                <small :class="differenceClass(estimate.work_or_billing_difference)">
                  変換元差額 {{ formatSignedCurrency(estimate.work_or_billing_difference) }}
                </small>
              </template>
              <span v-else>-</span>
            </td>
            <td>{{ estimate.created_by_user?.name || '-' }}</td>
          </tr>
          <tr v-if="!isLoading && listLoadFailed">
            <td colspan="12" class="text-center py-4 text-danger">
              見積書一覧を取得できませんでした。時間をおいて再度お試しください。
            </td>
          </tr>
          <tr v-else-if="!isLoading && estimates.length === 0">
            <td colspan="12" class="text-center py-4 text-muted">
              {{ approvalOnly ? '現在、承認待ちの見積はありません。' : '条件に該当する見積はありません。' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="removeEstimate"
    />
    <EstimateStatusTransitionModal
      ref="statusTransitionModal"
      :is-submitting="isTransitioning"
      @confirm="confirmApprovalAction"
    />
    <ConfirmationModal
      ref="confirmationModal"
      :is-submitting="isDuplicating"
      @confirm="confirmListAction"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  approveEstimate,
  copyEstimate,
  createEstimatePdfHistory,
  deleteEstimate,
  getEstimate,
  getEstimates,
  getSelectBoxBuildings,
  getSelectBoxCustomers,
  rejectEstimate
} from '@/api/axios';
import EstimateStatusTransitionModal from '@/components/estimates/EstimateStatusTransitionModal.vue';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import {
  approvalStatusLabels,
  conversionStatusLabels,
  estimateTypeLabels,
  extractApiErrors,
  formatCurrency,
  formatIsoDate,
  formatRevisionLabel,
  isEstimateWriterRole,
  outputLayoutTypeLabels,
  salesStatusLabels
} from '@/utils/estimates';
import { createEstimatePdf, downloadBlob } from '@/utils/estimatePdf';

const props = defineProps({
  approvalOnly: {
    type: Boolean,
    default: false
  }
});

const route = useRoute();
const router = useRouter();
const store = useStore();

const estimates = ref([]);
const customers = ref([]);
const buildings = ref([]);
const errorMessages = ref([]);
const masterErrorMessages = ref([]);
const isLoading = ref(false);
const listLoadFailed = ref(false);
const paramsIdToDelete = ref(null);
const statusTransitionModal = ref(null);
const transitionTarget = ref(null);
const isTransitioning = ref(false);
const isSearchExpanded = ref(false);
const generatingPdfId = ref(null);
const isDuplicating = ref(false);
const copyTarget = ref(null);
const confirmationModal = ref(null);
const tableWrapper = ref(null);
const meta = reactive({
  current_page: 1,
  total_pages: 1,
  total_count: 0
});

const defaultFilters = () => ({
  estimate_no: '',
  title: '',
  customer_id: '',
  building_id: '',
  approval_status: props.approvalOnly ? 'pending' : '',
  sales_status: '',
  conversion_status: '',
  estimate_type: '',
  output_layout_type: '',
  issue_date_from: '',
  issue_date_to: '',
  deleted_status: 'active',
  sort_by: '',
  sort_direction: ''
});

const filters = reactive(defaultFilters());
const approvalOnly = computed(() => props.approvalOnly);
const pageTitle = computed(() => approvalOnly.value ? '承認待ち一覧' : '見積書一覧');
const canCreate = computed(() => isEstimateWriterRole(store.state.userRole));
const allErrorMessages = computed(() => [
  ...masterErrorMessages.value,
  ...errorMessages.value
]);
const filteredBuildings = computed(() => {
  if (!filters.customer_id) return buildings.value;
  return buildings.value.filter(building =>
    String(building.m_customer_id) === String(filters.customer_id)
  );
});
const formatSignedCurrency = value => {
  const amount = Number(value || 0);
  return `${amount > 0 ? '+' : ''}${formatCurrency(amount)}`;
};
const differenceClass = value => Number(value || 0) === 0 ? 'text-success' : 'text-danger fw-bold';
const statusBadgeClass = (statusType, status) => [
  'status-badge',
  `status-badge--${statusType}-${status}`
];
const activeQuery = computed(() => {
  const query = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null) query[key] = value;
  });
  if (approvalOnly.value) query.list_origin = 'approval_pending';
  if (meta.current_page > 1) query.page = meta.current_page;
  return query;
});

watch(() => filters.customer_id, () => {
  const buildingExists = filteredBuildings.value.some(building =>
    String(building.id) === String(filters.building_id)
  );
  if (!buildingExists) filters.building_id = '';
});

const applyRouteQuery = () => {
  if (approvalOnly.value) {
    Object.assign(filters, defaultFilters());
    ['sort_by', 'sort_direction'].forEach(key => {
      if (route.query[key] !== undefined) filters[key] = route.query[key];
    });
  } else {
    Object.keys(filters).forEach(key => {
      if (route.query[key] !== undefined) filters[key] = route.query[key];
    });
  }
  meta.current_page = Math.max(Number(route.query.page || 1), 1);
};

const searchParams = () => {
  if (approvalOnly.value) {
    const params = {
      page: meta.current_page,
      approval_status: 'pending',
      deleted_status: 'active'
    };
    if (filters.sort_by) params.sort_by = filters.sort_by;
    if (filters.sort_direction) params.sort_direction = filters.sort_direction;
    return params;
  }

  const params = {
    page: meta.current_page
  };
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null) params[key] = value;
  });
  return params;
};

const fetchSearchOptions = async () => {
  masterErrorMessages.value = [];
  if (approvalOnly.value) {
    customers.value = [];
    buildings.value = [];
    return;
  }

  try {
    const [customerResponse, buildingResponse] = await Promise.all([
      getSelectBoxCustomers(),
      getSelectBoxBuildings()
    ]);
    customers.value = customerResponse.data || [];
    buildings.value = buildingResponse.data || [];
  } catch (error) {
    masterErrorMessages.value = [
      '検索条件の選択肢を読み込めませんでした。',
      ...extractApiErrors(error)
    ];
  }
};

const fetchEstimates = async () => {
  try {
    isLoading.value = true;
    listLoadFailed.value = false;
    errorMessages.value = [];
    const response = await getEstimates(searchParams());
    estimates.value = response.data.estimates || [];
    Object.assign(meta, response.data.meta || {});
  } catch (error) {
    estimates.value = [];
    Object.assign(meta, { current_page: 1, total_pages: 1, total_count: 0 });
    listLoadFailed.value = true;
    errorMessages.value = extractApiErrors(error);
  } finally {
    isLoading.value = false;
  }
};

const updateRoute = async () => {
  await router.replace({ name: route.name, query: activeQuery.value });
};

const search = async () => {
  meta.current_page = 1;
  await updateRoute();
  await fetchEstimates();
};

const clearSearch = async () => {
  Object.assign(filters, defaultFilters());
  meta.current_page = 1;
  await updateRoute();
  await fetchEstimates();
};

const changePage = async page => {
  meta.current_page = page;
  await updateRoute();
  await fetchEstimates();
};

const sortIndicator = column => {
  if (filters.sort_by !== column) return '';
  return filters.sort_direction === 'desc' ? '▼' : '▲';
};

const changeSort = async column => {
  if (filters.sort_by === column) {
    filters.sort_direction = filters.sort_direction === 'asc' ? 'desc' : 'asc';
  } else {
    filters.sort_by = column;
    filters.sort_direction = 'asc';
  }

  meta.current_page = 1;
  await updateRoute();
  await fetchEstimates();
};

const handleDeleteClick = estimateId => {
  paramsIdToDelete.value = estimateId;
};

const outputPdf = async estimate => {
  if (generatingPdfId.value !== null || !estimate.permissions?.can_output_pdf) return;

  try {
    generatingPdfId.value = estimate.id;
    errorMessages.value = [];
    const response = await getEstimate(estimate.id);
    const fullEstimate = response.data.estimate;
    const { blob, filename } = await createEstimatePdf(fullEstimate);
    const pdfFile = new File([blob], filename, { type: 'application/pdf' });
    await createEstimatePdfHistory(
      fullEstimate.id,
      pdfFile,
      fullEstimate.output_layout_type,
      fullEstimate.default_language_type
    );
    downloadBlob(blob, filename);
    store.dispatch('setFlashMessage', {
      message: '見積書PDFを作成し、出力履歴に保存しました',
      type: 'success'
    });
  } catch (error) {
    const messages = error?.response ? extractApiErrors(error) : [error.message];
    errorMessages.value = messages;
    store.dispatch('setFlashMessage', {
      message: messages[0] || 'PDFの作成に失敗しました',
      type: 'danger'
    });
  } finally {
    generatingPdfId.value = null;
  }
};

const showCopy = estimate => {
  if (!estimate.permissions?.can_copy) return;
  copyTarget.value = estimate;
  confirmationModal.value?.show({
    action: 'copy',
    title: '見積複製確認',
    description: `「${estimate.title}」を新しい見積番号の下書きとして複製します。よろしいですか？`,
    confirmLabel: '複製する',
    submittingLabel: '複製中...',
    confirmButtonClass: 'btn-success'
  });
};

const confirmListAction = async ({ action }) => {
  if (action !== 'copy' || !copyTarget.value || isDuplicating.value) return;
  const source = copyTarget.value;
  let created = null;

  try {
    isDuplicating.value = true;
    errorMessages.value = [];
    const response = await copyEstimate(source.id, source.lock_version);
    created = response.data.estimate;
    store.dispatch('setFlashMessage', {
      message: `見積 ${created.estimate_no} を複製しました`,
      type: 'success'
    });
  } catch (error) {
    const messages = extractApiErrors(error);
    errorMessages.value = messages;
    store.dispatch('setFlashMessage', {
      message: messages[0] || '見積の複製に失敗しました',
      type: error?.response?.status === 409 ? 'warning' : 'danger'
    });
  } finally {
    isDuplicating.value = false;
  }

  if (created) {
    await nextTick();
    confirmationModal.value?.closeModal();
    copyTarget.value = null;
    await router.push({
      name: 'EstimateEdit',
      params: { id: created.id },
      query: { ...activeQuery.value, edit_origin: 'list' }
    });
  }
};

const showApprove = estimate => {
  transitionTarget.value = estimate;
  statusTransitionModal.value?.show({
    action: 'approve',
    title: '見積承認確認',
    description: `「${estimate.title}」を承認します。承認コメントがあれば入力してください。`,
    inputLabel: '承認コメント（任意）',
    confirmLabel: '承認する',
    submittingLabel: '承認中...',
    confirmButtonClass: 'btn-primary'
  });
};

const showReject = estimate => {
  transitionTarget.value = estimate;
  statusTransitionModal.value?.show({
    action: 'reject',
    title: '見積差戻し確認',
    description: `「${estimate.title}」を差し戻します。差戻し理由を入力してください。`,
    inputLabel: '差戻し理由',
    isRequired: true,
    requiredMessage: '差戻し理由を入力してください',
    confirmLabel: '差し戻す',
    submittingLabel: '差戻し中...',
    confirmButtonClass: 'btn-danger'
  });
};

const confirmApprovalAction = async ({ action, value }) => {
  const estimate = transitionTarget.value;
  if (!estimate || isTransitioning.value) return;

  let completed = false;
  try {
    isTransitioning.value = true;
    errorMessages.value = [];
    if (action === 'approve') {
      await approveEstimate(estimate.id, estimate.lock_version, value);
    } else if (action === 'reject') {
      await rejectEstimate(estimate.id, estimate.lock_version, value);
    } else {
      return;
    }
    completed = true;
    store.dispatch('setFlashMessage', {
      message: action === 'approve' ? '見積を承認しました' : '見積を差し戻しました',
      type: 'success'
    });
    await fetchEstimates();
    store.dispatch('requestApprovalPendingCountRefresh');
  } catch (error) {
    const messages = extractApiErrors(error);
    errorMessages.value = messages;
    store.dispatch('setFlashMessage', {
      message: messages[0] || '承認状態の変更に失敗しました',
      type: error?.response?.status === 409 ? 'warning' : 'danger'
    });
  } finally {
    isTransitioning.value = false;
    if (completed) {
      await nextTick();
      statusTransitionModal.value?.closeModal();
      transitionTarget.value = null;
    }
  }
};

const removeEstimate = async estimateId => {
  const estimate = estimates.value.find(item => item.id === estimateId);
  if (!estimate) return;

  try {
    await deleteEstimate(estimate.id, estimate.lock_version);
    store.dispatch('setFlashMessage', {
      message: '見積を削除しました',
      type: 'success'
    });
    await fetchEstimates();
  } catch (error) {
    const messages = extractApiErrors(error);
    errorMessages.value = messages;
    store.dispatch('setFlashMessage', {
      message: messages[0] || '見積の削除に失敗しました',
      type: error?.response?.status === 409 ? 'warning' : 'danger'
    });
  }
};

onMounted(async () => {
  document.title = pageTitle.value;
  applyRouteQuery();

  await fetchSearchOptions();
  await fetchEstimates();
});

watch(approvalOnly, async () => {
  Object.assign(filters, defaultFilters());
  meta.current_page = 1;
  applyRouteQuery();
  await fetchSearchOptions();
  await fetchEstimates();
});
</script>

<style scoped>
.estimate-list {
  min-width: 0;
}

.estimate-table {
  min-width: 1380px;
  background: #fff;
  font-size: 13px;
}

.estimate-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #003366;
  color: #fff;
  white-space: nowrap;
}

.sort-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  white-space: nowrap;
}

.sort-button:hover,
.sort-button:focus-visible {
  text-decoration: underline;
}

.status-badge {
  display: inline-block;
  min-width: 70px;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid transparent;
  text-align: center;
  white-space: nowrap;
}

.status-badge--approval-draft,
.status-badge--sales-not_presented,
.status-badge--conversion-not_converted {
  border-color: #c8cdd2;
  background: #eef0f2;
  color: #495057;
}

.status-badge--approval-pending {
  border-color: #e2bd53;
  background: #fff3cd;
  color: #664d03;
}

.status-badge--approval-approved,
.status-badge--sales-ordered {
  border-color: #76b58b;
  background: #d1e7dd;
  color: #0f5132;
}

.status-badge--approval-rejected,
.status-badge--sales-lost {
  border-color: #dc8d95;
  background: #f8d7da;
  color: #842029;
}

.status-badge--sales-presented,
.status-badge--conversion-converted {
  border-color: #83b6dd;
  background: #cfe2ff;
  color: #084298;
}

.action-column {
  width: 1%;
  white-space: nowrap;
}

.estimate-table th.action-column,
.estimate-table td.action-column {
  position: sticky;
  left: 0;
}

.estimate-table th.action-column {
  z-index: 4;
}

.estimate-table td.action-column {
  z-index: 1;
  background: #fff;
}

.estimate-table .table-secondary td.action-column {
  background: var(--bs-table-bg);
}

.row-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
  white-space: nowrap;
}

.result-navigation {
  min-height: 44px;
}

@media (max-width: 767px) {
  .estimate-list {
    padding: 12px;
  }

  .estimate-list-header,
  .result-navigation {
    align-items: stretch !important;
    flex-direction: column;
  }

  .estimate-list-header > div,
  .result-navigation > div {
    justify-content: flex-start !important;
  }

  .estimate-list-header .btn {
    flex: 1 1 auto;
  }

}
</style>
